import React, { useMemo, useEffect, useState } from "react";

/**
 * Utility helpers
 */
const toNumber = (value) =>
    value === null || value === undefined ? 0 : Number(value);

const formatNumber = (value) => {
    const num = toNumber(value);
    if (num > 10) return Math.round(num).toLocaleString();
    return num.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

const formatPercent = (value) => `${Math.abs(toNumber(value)).toFixed(2)}%`;

const getChangeMeta = (percentChange) => {
    const value = toNumber(percentChange);
    if (value > 0) return { className: "positive", icon: "▲" };
    if (value < 0) return { className: "negative", icon: "▼" };
    return { className: "neutral", icon: "■" };
};

/**
 * Hook for screen size
 */
function useIsMobile() {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 600);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    return isMobile;
}

/**
 * TickerWidget Component
 */
export default function TickerWidget({ tickerData = [] }) {
    const isMobile = useIsMobile();

    /**
     * Filter out assets where all comment counts are 0
     */
    const filteredData = useMemo(() => {
        return tickerData
            .filter(item =>
                toNumber(item.last_day_coms) !== 0 ||
                toNumber(item.avg_coms_last_week) !== 0 ||
                toNumber(item.total_coms_last_week) !== 0
            )
            .sort((a, b) => toNumber(b.percent_change) - toNumber(a.percent_change));
    }, [tickerData]);

    if (!filteredData || filteredData.length === 0) {
        return (
            <div className="loading-container">
                <span className="loading-text">No data to display</span>
            </div>
        );
    }

    /**
     * MOBILE CARD LAYOUT
     */
    if (isMobile) {
        return (
            <div className="ticker-cards-container">
                {filteredData.map((item) => {
                    const { className, icon } = getChangeMeta(item.percent_change);
                    return (
                        <div className="ticker-card" key={item.id}>
                            <div className="ticker-card-header">
                                {item.last_name?.toUpperCase()}
                            </div>
                            <div className="ticker-card-body">
                                <div className="ticker-card-row">
                                    <span className="label">Last 24h</span>
                                    <span>{formatNumber(item.last_day_coms)}</span>
                                </div>
                                <div className="ticker-card-row">
                                    <span className="label">Week Avg</span>
                                    <span>{formatNumber(item.avg_coms_last_week)}</span>
                                </div>
                                <div className="ticker-card-row">
                                    <span className="label">Week Total</span>
                                    <span>{formatNumber(item.total_coms_last_week)}</span>
                                </div>
                                <div className={`ticker-card-row percent ${className}`}>
                                    <span className="label">% Change</span>
                                    <span>
                                        <span className="ticker-arrow">{icon}</span>
                                        {formatPercent(item.percent_change)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    }

    /**
     * DESKTOP TABLE
     */
    return (
        <div className="ticker-container">
            <table className="ticker-table">
                <thead>
                    <tr>
                        <th>Name</th>
                        <th>Last 24h</th>
                        <th>Week Avg</th>
                        <th>Week Total</th>
                        <th>% Change</th>
                    </tr>
                </thead>
                <tbody>
                    {filteredData.map((item) => {
                        const { className, icon } = getChangeMeta(item.percent_change);
                        return (
                            <tr key={item.id}>
                                <td className="ticker-name">{item.last_name?.toUpperCase()}</td>
                                <td>{formatNumber(item.last_day_coms)}</td>
                                <td>{formatNumber(item.avg_coms_last_week)}</td>
                                <td>{formatNumber(item.total_coms_last_week)}</td>
                                <td className={`ticker-change ${className}`}>
                                    <span className="ticker-arrow">{icon}</span>
                                    {formatPercent(item.percent_change)}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}